import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck, Award, CheckCircle2, Circle, Loader2, Plus, Save, Trash2,
  FileText, AlertTriangle, Trophy, TrendingUp, Upload, RefreshCw, BookOpen,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AurenConsejosTab } from "../components/auren/AurenConsejosTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ESTADOS = ["Done", "WIP", "NotS", "NA"];
const CATEGORIAS = [
  { key: "", label: "General" },
  { key: "auditoria_2024", label: "Documentación auditoría 2024" },
  { key: "normativa_iso", label: "Normativa ISO" },
  { key: "formacion_aenor", label: "Documentación formación aenor ISO" },
];

const ESTADO_STYLE = {
  Done: { color: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200", label: "Done", icon: CheckCircle2 },
  WIP:  { color: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200", label: "WIP", icon: Loader2 },
  NotS: { color: "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-200", label: "NotS", icon: Circle },
  NA:   { color: "bg-slate-100 text-slate-500 border-slate-300", label: "NA", icon: Circle },
};

function EstadoPill({ value }) {
  const s = ESTADO_STYLE[value] || ESTADO_STYLE.NA;
  const Icon = s.icon;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold ${s.color}`}><Icon className="w-3 h-3" />{s.label}</span>;
}

function ItemForm({ item, onSaved, onCancel, authH }) {
  const [d, setD] = useState(item || { name: "", estado: "NotS", categoria: "", year: 2026 });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!d.name?.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const url = item?.id ? `${API}/auren/items/${item.id}` : `${API}/auren/items`;
      const r = await fetch(url, {
        method: item?.id ? "PUT" : "POST",
        headers: { ...authH, "Content-Type": "application/json" },
        body: JSON.stringify(d),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Error");
      toast.success("Item guardado");
      onSaved();
    } catch (e) { toast.error(String(e?.message || "Error")); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3 text-sm" data-testid="auren-item-form">
      <div><Label>Nombre *</Label><Input value={d.name || ""} onChange={e => setD({...d, name: e.target.value})} data-testid="auren-item-name" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label>Número</Label><Input type="number" value={d.num ?? ""} onChange={e => setD({...d, num: e.target.value === "" ? null : parseInt(e.target.value)})} /></div>
        <div>
          <Label>Estado</Label>
          <Select value={d.estado} onValueChange={v => setD({...d, estado: v})}>
            <SelectTrigger data-testid="auren-item-estado"><SelectValue /></SelectTrigger>
            <SelectContent>{ESTADOS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label>Deadline</Label><Input type="date" value={d.deadline || ""} onChange={e => setD({...d, deadline: e.target.value || null})} /></div>
        <div><Label>Responsable</Label><Input value={d.responsable || ""} onChange={e => setD({...d, responsable: e.target.value})} /></div>
      </div>
      <div>
        <Label>Categoría</Label>
        <Select value={d.categoria || ""} onValueChange={v => setD({...d, categoria: v || null})}>
          <SelectTrigger><SelectValue placeholder="General" /></SelectTrigger>
          <SelectContent>{CATEGORIAS.map(c => <SelectItem key={c.key || "none"} value={c.key || "none"}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Acción 2026</Label><Textarea rows={2} value={d.accion || ""} onChange={e => setD({...d, accion: e.target.value})} /></div>
      <div><Label>Ubicación</Label><Input value={d.ubicacion || ""} onChange={e => setD({...d, ubicacion: e.target.value})} /></div>
      <div><Label>Observación</Label><Textarea rows={2} value={d.observacion || ""} onChange={e => setD({...d, observacion: e.target.value})} /></div>
      <div><Label>Notas</Label><Textarea rows={2} value={d.notas || ""} onChange={e => setD({...d, notas: e.target.value})} /></div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancelar</Button>
        <Button onClick={save} disabled={saving} data-testid="auren-item-save">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar</Button>
      </div>
    </div>
  );
}

export default function AurenPage() {
  const { token, user } = useAuth();
  const authH = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterEstado, setFilterEstado] = useState("all");
  const [filterCategoria, setFilterCategoria] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [it, sum] = await Promise.all([
        fetch(`${API}/auren/items?year=2026`, { headers: authH }).then(r => r.json()),
        fetch(`${API}/auren/summary?year=2026`, { headers: authH }).then(r => r.json()),
      ]);
      setItems(it);
      setSummary(sum);
    } catch { toast.error("Error cargando checklist ISO"); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { if (token) load(); }, [token, load]);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este item?")) return;
    try {
      await fetch(`${API}/auren/items/${id}`, { method: "DELETE", headers: authH });
      toast.success("Eliminado");
      await load();
    } catch { toast.error("Error"); }
  };

  const runSeed = async () => {
    if (!isAdmin) return;
    setSeeding(true);
    try {
      const r = await fetch(`${API}/auren/seed`, { method: "POST", headers: authH });
      const j = await r.json();
      toast.success(`${j.inserted} items sembrados, ${j.skipped} ya existían`);
      await load();
    } catch { toast.error("Error"); }
    finally { setSeeding(false); }
  };

  const uploadCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !isAdmin) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/auren/items/import-csv`, { method: "POST", headers: authH, body: fd });
      const j = await r.json();
      toast.success(`Importados ${j.inserted} nuevos, ${j.updated} actualizados`);
      await load();
    } catch { toast.error("Error importando CSV"); }
    event.target.value = "";
  };

  const filtered = items.filter(i => {
    if (filterEstado !== "all" && i.estado !== filterEstado) return false;
    if (filterCategoria !== "all") {
      if (filterCategoria === "_none" && i.categoria) return false;
      else if (filterCategoria !== "_none" && i.categoria !== filterCategoria) return false;
    }
    if (search && !((i.name || "") + " " + (i.responsable || "")).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="auren-page">
      <Tabs defaultValue="checklist">
        <TabsList className="grid grid-cols-2 w-full max-w-md" data-testid="auren-tabs">
          <TabsTrigger value="checklist" data-testid="auren-tab-checklist">
            <ShieldCheck className="w-4 h-4 mr-1" />Checklist
          </TabsTrigger>
          <TabsTrigger value="consejos" data-testid="auren-tab-consejos">
            <BookOpen className="w-4 h-4 mr-1" />Consejos del auditor
          </TabsTrigger>
        </TabsList>

        <TabsContent value="consejos" className="mt-4">
          <AurenConsejosTab />
        </TabsContent>

        <TabsContent value="checklist" className="space-y-6 mt-4">
      <div className="rounded-2xl border bg-gradient-to-br from-slate-50 via-white to-violet-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950/30 p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-violet-700 font-semibold mb-1 flex items-center gap-1.5">
              <Award className="w-3 h-3" /> Sistema de Gestión I+D+i 56001
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Checklist ISO 56001 · Auditoría 2026</h1>
            <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
              Seguimiento del sistema de gestión de innovación alineado con ISO 56001:2024 y previa auditoría Aenor.
            </p>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <label className="cursor-pointer" data-testid="auren-import-csv-label">
                <input type="file" accept=".csv" className="hidden" onChange={uploadCSV} data-testid="auren-import-csv" />
                <Button variant="outline" size="sm" asChild><span><Upload className="w-3.5 h-3.5 mr-1.5" />Importar CSV</span></Button>
              </label>
              <Button variant="outline" size="sm" onClick={runSeed} disabled={seeding} data-testid="auren-seed">
                {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}Cargar plantilla 2026
              </Button>
              <Button size="sm" onClick={() => setEditing("new")} data-testid="auren-add-btn"><Plus className="w-3.5 h-3.5 mr-1.5" />Nuevo item</Button>
            </div>
          )}
        </div>

        {summary && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="auren-kpis">
            <KpiCard label="Total items" value={summary.total} color="slate" />
            <KpiCard label="Done" value={summary.by_estado.Done || 0} color="emerald" />
            <KpiCard label="WIP" value={summary.by_estado.WIP || 0} color="amber" />
            <KpiCard label="NotS" value={summary.by_estado.NotS || 0} color="red" />
            <KpiCard label="% Checklist ISO 56001" value={`${summary.pct_done || 0}%`} color="violet" bigValue />
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-2" data-testid="auren-vs-pg07-note">
          Este % mide items de la checklist de auditoría ISO 56001 (Done/Total). No es el mismo indicador
          que "Cumplimiento PG-07" (fichas F.01/F.02/seguimiento por proyecto en /pg07) — pueden diferir.
        </p>
        {summary && summary.overdue > 0 && (
          <div className="mt-3 flex items-center gap-2 text-xs text-red-700 dark:text-red-300">
            <AlertTriangle className="w-4 h-4" />
            <span><strong>{summary.overdue}</strong> item{summary.overdue !== 1 && "s"} con deadline vencido</span>
          </div>
        )}
      </div>

      {/* Responsables ranking */}
      {summary?.ranking_responsables?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="w-3.5 h-3.5" /> Ranking cumplimiento por responsable
            </div>
            <div className="space-y-1.5">
              {summary.ranking_responsables.slice(0, 8).map((r, i) => (
                <div key={r.responsable} className="flex items-center gap-3 py-1" data-testid={`auren-resp-${i}`}>
                  <span className="w-6 text-center text-xs font-bold text-muted-foreground">{i+1}</span>
                  <span className="flex-1 text-sm font-medium truncate">{r.responsable}</span>
                  <span className="text-xs text-emerald-700">{r.done} done</span>
                  <span className="text-xs text-amber-700">{r.wip} wip</span>
                  <span className="text-xs text-red-700">{r.notS} notS</span>
                  <span className="w-32 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded overflow-hidden">
                      <div className={`h-full ${r.pct_done >= 80 ? "bg-emerald-500" : r.pct_done >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${r.pct_done}%` }} />
                    </div>
                    <span className="text-xs font-bold w-10 text-right">{r.pct_done}%</span>
                    {i === 0 && r.pct_done >= 80 && <Trophy className="w-3 h-3 text-amber-500" />}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center flex-wrap gap-2">
        <Input placeholder="Buscar por nombre o responsable..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" data-testid="auren-search" />
        <Select value={filterEstado} onValueChange={setFilterEstado}>
          <SelectTrigger className="w-40" data-testid="auren-filter-estado"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {ESTADOS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCategoria} onValueChange={setFilterCategoria}>
          <SelectTrigger className="w-64" data-testid="auren-filter-categoria"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            <SelectItem value="_none">General</SelectItem>
            {CATEGORIAS.filter(c => c.key).map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-muted-foreground" data-testid="auren-count">{filtered.length} de {items.length}</div>
      </div>

      {/* List */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="auren-table">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-3 py-2 w-8">#</th>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-3 py-2 w-32">Deadline</th>
                <th className="text-left px-3 py-2 w-40">Responsable</th>
                <th className="text-center px-3 py-2 w-20">Estado</th>
                {isAdmin && <th className="w-24"></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((it, i) => {
                const today = new Date().toISOString().slice(0, 10);
                const overdue = it.deadline && it.deadline < today && it.estado !== "Done";
                return (
                  <tr key={it.id} className="border-b hover:bg-muted/40 transition-colors" data-testid={`auren-row-${i}`}>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{it.num ?? "—"}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{it.name}</div>
                      {it.accion && <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[500px]">{it.accion}</div>}
                      {it.categoria && <Badge variant="outline" className="mt-0.5 text-[9px]">{CATEGORIAS.find(c => c.key === it.categoria)?.label || it.categoria}</Badge>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {it.deadline || "—"}
                      {overdue && <Badge variant="destructive" className="ml-1 text-[9px]">Vencido</Badge>}
                    </td>
                    <td className="px-3 py-2 text-xs">{it.responsable || "—"}</td>
                    <td className="px-3 py-2 text-center"><EstadoPill value={it.estado} /></td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(it)} data-testid={`auren-edit-${i}`}>Editar</Button>
                        <Button size="sm" variant="ghost" onClick={() => del(it.id)}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 6 : 5} className="p-8 text-center text-sm text-muted-foreground">Sin items</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="auren-dialog">
          <DialogHeader><DialogTitle>{editing === "new" ? "Nuevo item ISO 56001" : "Editar item"}</DialogTitle></DialogHeader>
          {editing !== null && (
            <ItemForm item={editing === "new" ? null : editing} authH={authH}
              onSaved={() => { setEditing(null); load(); }}
              onCancel={() => setEditing(null)} />
          )}
        </DialogContent>
      </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KpiCard({ label, value, color = "slate", bigValue = false }) {
  return (
    <div className={`rounded-xl border p-3 bg-${color}-50/60 dark:bg-${color}-950/20`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`${bigValue ? "text-3xl" : "text-2xl"} font-black text-${color}-700 dark:text-${color}-300 mt-1`}>{value}</div>
    </div>
  );
}
